// src/services/waterOverpass.ts
// Overpass helper for "snap hotspots to water" (best effort, production-hardened).
//
// v4 improvements:
// - keeps the same cache/throttle/fallback model
// - requests lightweight geometry when available
// - snaps to nearest line/polygon vertex/segment instead of feature center only
// - keeps center fallback when geometry is unavailable
// - preserves attachWaterName + waterKind compatibility for existing MapView/FishIQ UI

import {
  hasCoastalSignal,
  hasFreshwaterSignal,
  resolveWaterContext,
} from './fishIQWaterContext';

export type LatLng = { lat: number; lng: number };

export type WaterFeature = {
  id: string;
  kind: 'water' | 'reservoir' | 'waterway';
  name?: string;
  center: LatLng;
  geometry?: LatLng[];
  waterKind?: string;
  waterType?: string;
  tags?: Record<string, any>;
  quality?: 'named-inland' | 'named-coastal' | 'named-water' | 'generic-water';
  isGenericName?: boolean;
};

const OVERPASS_ENDPOINTS = [
  // Prefer the Kumi round-robin endpoint first; it is explicitly intended for
  // public project use. Keep two official overpass-api.de backends as failover
  // without paying for an initial redirect through the generic hostname.
  'https://overpass.kumi.systems/api/interpreter',
  'https://lz4.overpass-api.de/api/interpreter',
  'https://z.overpass-api.de/api/interpreter',
];

const CACHE_PREFIX = 'roddo.fishiQ.waterFeatures.v4';
let lastNetworkQueryAt = 0;
let lastNetworkFeatures: WaterFeature[] | null = null;

const LAST_GOOD_KEY = `${CACHE_PREFIX}.lastGood`;
const DEFAULT_TTL_MS = 45 * 60 * 1000;
const MIN_ZOOM_TO_QUERY = 12;
const MAX_LAT_SPAN_DEG = 1.15;
const MAX_LNG_SPAN_DEG = 1.35;
const FETCH_TIMEOUT_MS = 12000;

type BoundsLike = {
  getSouthWest: () => { lat: number; lng: number };
  getNorthEast: () => { lat: number; lng: number };
};

function round5(n: number) {
  return Math.round(n * 1e5) / 1e5;
}

function boundsKey(bounds: BoundsLike, zoom: number) {
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  return [
    CACHE_PREFIX,
    'z',
    zoom,
    'sw',
    round5(sw.lat),
    round5(sw.lng),
    'ne',
    round5(ne.lat),
    round5(ne.lng),
  ].join(':');
}

function safeToQuery(bounds: BoundsLike, zoom: number) {
  if (!Number.isFinite(zoom) || zoom < MIN_ZOOM_TO_QUERY) return false;
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  const latSpan = Math.abs(ne.lat - sw.lat);
  const lngSpan = Math.abs(ne.lng - sw.lng);
  if (latSpan > MAX_LAT_SPAN_DEG) return false;
  if (lngSpan > MAX_LNG_SPAN_DEG) return false;
  return true;
}

function withTimeout(ms: number) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), ms);
  return { controller, timer };
}


const GENERIC_WATER_NAME_RE = /\b(water|waterbody|reservoir|lake|river|stream|canal|pond|basin|drain|ditch|sea|ocean|bay|coastline|unnamed)\b/i;
const HUGE_MARINE_NAME_RE = /\b(pacific ocean|atlantic ocean|indian ocean|arctic ocean|southern ocean|south china sea|philippine sea|java sea|banda sea|celebes sea|sulu sea|flores sea|andaman sea|bali sea)\b/i;

function tidyName(value: any): string | undefined {
  const name = String(value || '').replace(/\s+/g, ' ').trim();
  if (!name || name.length < 3) return undefined;
  return name;
}

function pickName(tags: any): string | undefined {
  if (!tags) return undefined;
  return (
    tidyName(tags.name) ||
    tidyName(tags['name:en']) ||
    tidyName(tags['name:local']) ||
    tidyName(tags.alt_name) ||
    undefined
  );
}

function isGenericWaterName(name?: string): boolean {
  if (!name) return true;
  const cleaned = name.trim();
  if (!cleaned) return true;
  if (HUGE_MARINE_NAME_RE.test(cleaned)) return true;
  // One-word labels like "Lake" or "River" are not useful metadata. "Lake Toba" is useful.
  return GENERIC_WATER_NAME_RE.test(cleaned) && cleaned.split(/\s+/).length <= 2;
}

function toGeometry(el: any): LatLng[] | undefined {
  const raw = Array.isArray(el?.geometry) ? el.geometry : [];
  const pts = raw
    .map((p: any) => ({ lat: Number(p?.lat), lng: Number(p?.lon) }))
    .filter((p: LatLng) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  return pts.length >= 2 ? pts : undefined;
}


function normalizeFeatureWaterKind(tags: any): string {
  const water = String(tags?.water || '').toLowerCase();
  const waterway = String(tags?.waterway || '').toLowerCase();
  const natural = String(tags?.natural || '').toLowerCase();
  const name = String(tags?.name || tags?.['name:en'] || '').toLowerCase();

  if (/^(river|stream|canal|ditch|drain|brook)$/.test(waterway)) return waterway;
  if (waterway) return waterway;
  if (water === 'reservoir' || /reservoir|dam/.test(name)) return 'reservoir';
  if (water === 'pond' || /pond|basin/.test(name)) return 'pond';
  if (water === 'lake' || /\blake\b/.test(name)) return 'lake';
  if (water === 'river' || /\briver\b/.test(name)) return 'river';
  if (water === 'canal' || /canal|aqueduct/.test(name)) return 'canal';
  if (water === 'lagoon' || /lagoon/.test(name)) return 'lagoon';
  if (water === 'bay' || /\bbay\b/.test(name)) return 'bay';
  if (water === 'sea' || water === 'ocean' || /\b(sea|ocean)\b/.test(name)) return water || 'ocean';
  if (natural === 'bay') return 'bay';
  return water || 'water';
}

function featureQuality(name: string | undefined, waterKind: string): WaterFeature['quality'] {
  const hay = `${name || ''} ${waterKind}`;
  if (hasFreshwaterSignal(hay)) return name && !isGenericWaterName(name) ? 'named-inland' : 'generic-water';
  if (hasCoastalSignal(hay)) return name && !isGenericWaterName(name) ? 'named-coastal' : 'generic-water';
  if (name && !isGenericWaterName(name)) return 'named-water';
  return 'generic-water';
}

function shouldKeepFeature(name: string | undefined, waterKind: string, tags: any): boolean {
  const hay = `${name || ''} ${waterKind} ${tags?.natural || ''} ${tags?.water || ''} ${tags?.waterway || ''}`.toLowerCase();
  const isHugeMarine = HUGE_MARINE_NAME_RE.test(hay) || /\b(ocean|sea)\b/.test(waterKind);
  if (isHugeMarine && !/\b(bay|lagoon|estuary|harbor|harbour|marina|mangrove|port|inlet|strait|gulf)\b/.test(hay)) {
    return false;
  }
  return true;
}

function parseOverpass(json: any): WaterFeature[] {
  const els: any[] = Array.isArray(json?.elements) ? json.elements : [];
  const out: WaterFeature[] = [];

  for (const el of els) {
    const center = el.center
      ? { lat: Number(el.center.lat), lng: Number(el.center.lon) }
      : el.lat != null && el.lon != null
        ? { lat: Number(el.lat), lng: Number(el.lon) }
        : null;

    if (!center || !Number.isFinite(center.lat) || !Number.isFinite(center.lng)) continue;

    const tags = el.tags || {};
    const kind: WaterFeature['kind'] =
      tags.water === 'reservoir' ? 'reservoir' : tags.waterway ? 'waterway' : 'water';

    const waterKind = normalizeFeatureWaterKind(tags);
    const name = pickName(tags);

    if (!shouldKeepFeature(name, waterKind, tags)) continue;

    out.push({
      id: `${el.type}/${el.id}`,
      kind,
      name,
      waterKind,
      waterType: waterKind,
      center,
      geometry: toGeometry(el),
      tags,
      quality: featureQuality(name, waterKind),
      isGenericName: isGenericWaterName(name),
    });
  }

  const seen = new Set<string>();
  const uniq: WaterFeature[] = [];
  for (const f of out) {
    if (seen.has(f.id)) continue;
    seen.add(f.id);
    uniq.push(f);
  }
  return uniq;
}

async function fetchOverpassJson(query: string): Promise<any> {
  let lastErr: any = null;

  for (const endpoint of OVERPASS_ENDPOINTS) {
    const { controller, timer } = withTimeout(FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body: `data=${encodeURIComponent(query)}`,
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`Overpass ${endpoint} failed: ${res.status} ${res.statusText}`);
      const txt = await res.text();
      try {
        return JSON.parse(txt);
      } catch {
        throw new Error(`Overpass ${endpoint} returned non-JSON response`);
      }
    } catch (e) {
      lastErr = e;
    } finally {
      window.clearTimeout(timer);
    }
  }

  throw lastErr || new Error('Overpass failed (all endpoints)');
}

function buildBboxQuery(sw: LatLng, ne: LatLng, light = false) {
  const south = sw.lat;
  const west = sw.lng;
  const north = ne.lat;
  const east = ne.lng;

  const waterWays = light
    ? ''
    : `  way["waterway"~"river|stream|canal|ditch|drain"](${south},${west},${north},${east});\n  relation["waterway"~"river|stream|canal|ditch|drain"](${south},${west},${north},${east});\n`;

  const outCap = light ? 80 : 140;
  const timeoutSec = light ? 6 : 10;

  return `
[out:json][timeout:${timeoutSec}];
(
  way["natural"="water"](${south},${west},${north},${east});
  relation["natural"="water"](${south},${west},${north},${east});
  way["water"="reservoir"](${south},${west},${north},${east});
  relation["water"="reservoir"](${south},${west},${north},${east});
${waterWays});
out center geom ${outCap};
`.trim();
}


function boundsSnapshot(bounds: BoundsLike) {
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  return {
    center: { lat: (sw.lat + ne.lat) / 2, lng: (sw.lng + ne.lng) / 2 },
    latSpan: Math.abs(ne.lat - sw.lat),
    lngSpan: Math.abs(ne.lng - sw.lng),
  };
}

function isLastGoodCompatible(parsed: any, bounds: BoundsLike): boolean {
  if (!parsed || !Array.isArray(parsed.features) || !parsed.bounds?.center) return false;
  const current = boundsSnapshot(bounds);
  const prior = parsed.bounds;
  const latDelta = Math.abs(Number(prior.center.lat) - current.center.lat);
  const lngScale = Math.max(0.2, Math.cos((current.center.lat * Math.PI) / 180));
  const lngDelta = Math.abs(Number(prior.center.lng) - current.center.lng) * lngScale;
  const approxKm = Math.sqrt(latDelta * latDelta + lngDelta * lngDelta) * 111;
  const maxCompatibleKm = Math.max(8, Math.min(35, Math.max(current.latSpan, current.lngSpan) * 111 * 0.9));
  return approxKm <= maxCompatibleKm;
}

export async function getWaterFeaturesCached(
  bounds: BoundsLike,
  zoom: number,
  opts?: { ttlMs?: number; allowLastGoodFallback?: boolean; force?: boolean; minIntervalMs?: number },
): Promise<WaterFeature[]> {
  const ttlMs = Math.max(5 * 60 * 1000, Number(opts?.ttlMs ?? DEFAULT_TTL_MS));
  const allowLastGoodFallback = opts?.allowLastGoodFallback !== false;
  const force = opts?.force === true;
  const minIntervalMs = Math.max(10_000, Number(opts?.minIntervalMs ?? 45_000));
  const key = boundsKey(bounds, zoom);

  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.features) && typeof parsed.at === 'number' && Date.now() - parsed.at < ttlMs) {
        return parsed.features as WaterFeature[];
      }
    }
  } catch {}

  if (!safeToQuery(bounds, zoom)) {
    if (allowLastGoodFallback) {
      try {
        const raw = localStorage.getItem(LAST_GOOD_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (isLastGoodCompatible(parsed, bounds)) return parsed.features as WaterFeature[];
        }
      } catch {}
    }
    return [];
  }

  if (!force) {
    const now = Date.now();
    if (now - lastNetworkQueryAt < minIntervalMs) {
      if (Array.isArray(lastNetworkFeatures)) return lastNetworkFeatures;
      if (allowLastGoodFallback) {
        try {
          const raw = localStorage.getItem(LAST_GOOD_KEY);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (isLastGoodCompatible(parsed, bounds)) return parsed.features as WaterFeature[];
          }
        } catch {}
      }
      return [];
    }
  }

  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  const query = buildBboxQuery({ lat: sw.lat, lng: sw.lng }, { lat: ne.lat, lng: ne.lng }, false);

  let json: any = null;
  try {
    lastNetworkQueryAt = Date.now();
    json = await fetchOverpassJson(query);
  } catch (e) {
    try {
      const light = buildBboxQuery({ lat: sw.lat, lng: sw.lng }, { lat: ne.lat, lng: ne.lng }, true);
      lastNetworkQueryAt = Date.now();
      json = await fetchOverpassJson(light);
    } catch (e2) {
      if (allowLastGoodFallback) {
        try {
          const raw = localStorage.getItem(LAST_GOOD_KEY);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (isLastGoodCompatible(parsed, bounds)) return parsed.features as WaterFeature[];
          }
        } catch {}

        // Water snapping is best-effort enrichment. If every public endpoint
        // and the light retry fail, return no enrichment rather than throwing
        // through MapView. The caller already has a deterministic base read.
        lastNetworkFeatures = [];
        return [];
      }
      throw e2;
    }
  }

  const features = parseOverpass(json);
  lastNetworkQueryAt = Date.now();
  lastNetworkFeatures = features;

  try {
    localStorage.setItem(key, JSON.stringify({ at: Date.now(), features }));
    localStorage.setItem(LAST_GOOD_KEY, JSON.stringify({ at: Date.now(), features, bounds: boundsSnapshot(bounds) }));
  } catch {}

  return features;
}

function haversineMeters(a: LatLng, b: LatLng) {
  const R = 6371000;
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sin1 = Math.sin(dLat / 2);
  const sin2 = Math.sin(dLng / 2);
  const h = sin1 * sin1 + Math.cos(lat1) * Math.cos(lat2) * sin2 * sin2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

function projectLatLng(p: LatLng) {
  const latRad = (p.lat * Math.PI) / 180;
  return {
    x: p.lng * 111320 * Math.cos(latRad),
    y: p.lat * 110540,
  };
}

function unprojectXY(x: number, y: number, refLat: number): LatLng {
  const cosLat = Math.max(0.2, Math.cos((refLat * Math.PI) / 180));
  return {
    lat: y / 110540,
    lng: x / (111320 * cosLat),
  };
}

function closestPointOnSegment(p: LatLng, a: LatLng, b: LatLng): LatLng {
  const pp = projectLatLng(p);
  const aa = projectLatLng(a);
  const bb = projectLatLng(b);
  const abx = bb.x - aa.x;
  const aby = bb.y - aa.y;
  const ab2 = abx * abx + aby * aby;
  if (ab2 <= 1e-6) return a;
  const apx = pp.x - aa.x;
  const apy = pp.y - aa.y;
  const t = Math.max(0, Math.min(1, (apx * abx + apy * aby) / ab2));
  return unprojectXY(aa.x + abx * t, aa.y + aby * t, p.lat);
}

function closestPointOnGeometry(p: LatLng, geom?: LatLng[]): LatLng | null {
  if (!Array.isArray(geom) || geom.length === 0) return null;
  if (geom.length === 1) return geom[0];

  let best: LatLng | null = null;
  let bestDist = Infinity;
  for (let i = 0; i < geom.length - 1; i += 1) {
    const c = closestPointOnSegment(p, geom[i], geom[i + 1]);
    const d = haversineMeters(p, c);
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  return best;
}


function geometryBias(kind: WaterFeature['kind']) {
  if (kind === 'waterway') return -42;
  if (kind === 'reservoir') return -18;
  return 0;
}

function featureQualityBias(feature: WaterFeature) {
  const waterKind = String(feature.waterKind || feature.kind || '').toLowerCase();
  let bias = 0;
  if (feature.quality === 'named-inland') bias -= 90;
  else if (feature.quality === 'named-water') bias -= 45;
  else if (feature.quality === 'named-coastal') bias -= 18;
  else bias += 70;

  if (/river|stream|creek|canal|reservoir|lake|pond|dam|basin|wetland/.test(waterKind)) bias -= 35;
  if (/ocean|sea/.test(waterKind)) bias += 220;
  if (feature.isGenericName) bias += 80;
  return bias;
}

function waterTruthConfidence(feature: WaterFeature): string {
  const resolved = resolveWaterContext({
    waterName: feature.name,
    waterKind: feature.waterKind,
    waterType: feature.waterType,
  });
  if (feature.name && !feature.isGenericName && resolved.freshwaterSignal) return 'confirmed-named-freshwater';
  if (feature.name && !feature.isGenericName && resolved.coastalSignal) return 'confirmed-named-coastal';
  if (feature.name && !feature.isGenericName) return 'confirmed-named-water-feature';
  return 'nearby-generic-water-feature';
}

export function snapHotspotsToWaterSpread<T extends { position: LatLng; name?: string }>(
  hotspots: T[],
  features: WaterFeature[],
  opts?: {
    maxSnapMeters?: number;
    attachWaterName?: boolean;
    minSeparationMeters?: number;
  },
): T[] {
  const maxSnapMeters = Math.max(50, Number(opts?.maxSnapMeters ?? 900));
  const attachWaterName = opts?.attachWaterName !== false;
  const minSep = Math.max(80, Number(opts?.minSeparationMeters ?? 260));

  if (!features || features.length === 0) return hotspots;

  const usedPoints: LatLng[] = [];
  const usedFeatureIds = new Map<string, number>();
  const tooCloseToUsed = (p: LatLng) => usedPoints.some((u) => haversineMeters(p, u) < minSep);

  return hotspots.map((h) => {
    const here = h.position;

    const ranked = features
      .map((f) => {
        const snapPoint = closestPointOnGeometry(here, f.geometry) || f.center;
        const d = haversineMeters(here, snapPoint);
        const reuseCount = usedFeatureIds.get(f.id) || 0;
        const separationPenalty = tooCloseToUsed(snapPoint) ? 5000 : 0;
        const reusePenalty = reuseCount * 180;
        const score = d + geometryBias(f.kind) + featureQualityBias(f) + separationPenalty + reusePenalty;
        return { f, d, score, snapPoint, reuseCount, separationPenalty };
      })
      .sort((a, b) => a.score - b.score);

    const viable = ranked.filter((r) => r.d <= maxSnapMeters);
    const chosen = viable.find((r) => r.separationPenalty === 0) || viable[0] || null;

    if (!chosen) return h;

    usedPoints.push(chosen.snapPoint);
    usedFeatureIds.set(chosen.f.id, (usedFeatureIds.get(chosen.f.id) || 0) + 1);

    const next: any = {
      ...h,
      position: { ...chosen.snapPoint },
      snapDistanceMeters: Math.round(chosen.d),
    };
    if (attachWaterName) {
      next.waterName = chosen.f.name || next.waterName;
      next.waterKind = chosen.f.waterKind || chosen.f.kind;
      next.waterType = chosen.f.waterType || chosen.f.waterKind || chosen.f.kind;
      next.waterTruthConfidence = waterTruthConfidence(chosen.f);
    }
    return next as T;
  });
}
